import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import React, { useCallback, useState } from 'react';
import { useHrpsApi } from '../../clients/useHrpsApi';
import { ClaimCategory, ClaimType } from '../../redux/types';
import { ClaimTemplateDocument, GenerateClaimTemplateInput, GenerateClaimTemplateOutput } from '../../types';
import { Logger } from '../../utils/logger';
import { useDownloader } from '../../utils/useDownloader';
import { useTranslation } from '../blocks';
import { useModal } from './Modal';
import { ProgressLoader } from './ProgressLoader';

const CONTRACTUAL_CLAIM_WAIT_SECONDS = 7;
const PROMOTIONAL_CLAIM_WAIT__SECONDS = 4;

type TemplateSelectionPopupProps = {
    resellerId: string;
    countryCode: string;
};

export const TemplateSelectionPopup = ({ resellerId, countryCode }: TemplateSelectionPopupProps) => {
    const [claimCategory, setClaimCategory] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [templates, setTemplates] = useState<ClaimTemplateDocument[]>([]);
    const [isLoaderVisible, setIsLoaderVisible] = useState(false);
    const [templateUrl, setTemplateUrl] = useState('');
    const [error, setError] = useState('');
    const modal = useModal();
    const { download } = useDownloader();
    const { t } = useTranslation('uploadClaimPage');
    const { t: tCommon } = useTranslation('common');

    const {
        claims: { generateClaimTemplate, getClaimUploadTemplates },
    } = useHrpsApi();

    const loadStaticTemplates = async (): Promise<ClaimTemplateDocument[]> => {
        if (templates.length > 0) return templates;

        const result = await getClaimUploadTemplates();
        return result.templates;
    };

    const handleDownload = async () => {
        if (!claimCategory) return;

        setError('');
        setIsLoaderVisible(true);
        setIsLoading(true);
        try {
            if (claimCategory === ClaimCategory.SCHEDULED_CONTRACTUAL) {
                await downloadFmdfTemplate();
            } else {
                const templates = await loadStaticTemplates();
                setTemplates(templates);
                downloadTprTemplate(templates);
            }
        } catch (error) {
            Logger.error('Failed to obtain templates', error);
            setError(t('templateSelection-downloadFailedError'));
        } finally {
            setIsLoading(false);
        }
    };

    const onFinished = useCallback(() => {
        setIsLoaderVisible(false);
        if (templateUrl) {
            download(templateUrl);
            modal.close();
        }
    }, [download, modal, templateUrl]);

    const getEstimatedTime = () => {
        return claimCategory === ClaimCategory.SCHEDULED_CONTRACTUAL
            ? CONTRACTUAL_CLAIM_WAIT_SECONDS
            : PROMOTIONAL_CLAIM_WAIT__SECONDS;
    };

    const getGenerateClaimTemplatePayload = (): GenerateClaimTemplateInput => {
        return {
            claimCategory: ClaimCategory.SCHEDULED_CONTRACTUAL,
            claimType: ClaimType.HARD_DOLLARS,
            preFilled: false,
            resellerId: resellerId,
        };
    };

    const downloadFmdfTemplate = async () => {
        const payload = getGenerateClaimTemplatePayload();
        const result: GenerateClaimTemplateOutput = await generateClaimTemplate(payload);
        const url = result?.claimUploadTemplate?.url;
        if (url) {
            setTemplateUrl(url);
        } else {
            Logger.error('Empty template no contracts found for reseller', result);
            setError(t('templateSelection-noContractsForReseller'));
        }
    };

    const downloadTprTemplate = (templates: ClaimTemplateDocument[]) => {
        if (!templates || templates.length === 0) {
            Logger.warn('No static templates available');
            setError(t('templateSelection-noTemplatesError'));
            return;
        }
        const countrySpecificTemplates = templates.filter((t) => t.countryCode === countryCode);
        const defaultTemplates = templates.filter((t) => t.countryCode === null);
        const relevantTemplates = countrySpecificTemplates.length ? countrySpecificTemplates : defaultTemplates;

        if (relevantTemplates.length < 1) {
            setError(t('templateSelection-noTemplatesError'));
            return;
        }
        setTemplateUrl(relevantTemplates[0].presignedURL);
    };

    return (
        <Column spacingInset={'300'}>
            {isLoaderVisible ? (
                <ProgressLoader
                    isLoading={isLoading}
                    estimatedTime={getEstimatedTime()}
                    onFinished={onFinished}
                    infoMessage={t('templateSelection-downloadingMessage')}
                />
            ) : (
                <>
                    <Text type={'b300'}>{t('templateSelection-instructions')}</Text>
                    <Select
                        value={claimCategory}
                        onChange={setClaimCategory}
                        size={'medium'}
                        label={t('templateSelection-claimCategoryLabel')}
                        placeholder={t('templateSelection-selectPlaceholder')}
                        disabled={isLoading}
                    >
                        <SelectOption value={ClaimCategory.PROMOTIONAL} label={t('claimCategory_promotional')} />
                        <SelectOption
                            value={ClaimCategory.SCHEDULED_CONTRACTUAL}
                            label={t('claimCategory_contractual')}
                        />
                    </Select>
                    {error && (
                        <Alert type={'error'} size={'large'}>
                            {error}
                        </Alert>
                    )}
                    <Button size={'medium'} onClick={handleDownload} disabled={isLoading}>
                        {tCommon('download-text')}
                    </Button>
                </>
            )}
        </Column>
    );
};
